import type { AppWithFavoriteStatus } from '@/api-types';
import { useAuthGuard } from './useAuthGuard';
import { useAppsData } from '@/contexts/apps-data-context';
import { browserDB } from '@/services/browser-storage/indexedDB';
import { useAuthStore } from '@/stores/authStore';

interface AppHookState<T> {
  apps: T[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}


export function useApps(): AppHookState<AppWithFavoriteStatus> {
  const { allApps, loading, error, refetchAllApps } = useAppsData();
  
  return {
    apps: allApps,
    loading: loading.allApps,
    error: error.allApps,
    refetch: refetchAllApps,
  };
}

export function useRecentApps() {
  const { recentApps, moreRecentAvailable, loading, error, refetchAllApps } = useAppsData();
  
  return { 
    apps: recentApps, 
    moreAvailable: moreRecentAvailable,
    loading: loading.allApps, 
    error: error.allApps, 
    refetch: refetchAllApps
  };
}

export function useFavoriteApps(): AppHookState<AppWithFavoriteStatus> {
  const { favoriteApps, loading, error, refetchFavoriteApps } = useAppsData();
  
  return {
    apps: favoriteApps,
    loading: loading.favoriteApps,
    error: error.favoriteApps,
    refetch: refetchFavoriteApps,
  };
}


export async function toggleFavorite(appId: string): Promise<boolean> {
  try {
    const authStore = useAuthStore.getState();
    const user = authStore.user;

    if (!user) {
      throw new Error('User must be authenticated to toggle favorites');
    }

    // For CSR architecture, we'll store favorites in browser storage
    // This is a simplified implementation - in a real app you might want
    // to sync favorites across devices
    const favorites = await browserDB.getFavoriteApps(user.id);
    // Determine numeric projectId from appId (supports numeric IDs and agentId strings)
    let projectIdNum: number | null = null;
    const numeric = parseInt(appId, 10);
    if (!Number.isNaN(numeric)) {
      projectIdNum = numeric;
    } else {
      const project = await browserDB.getProject(appId);
      if (project?.id != null) projectIdNum = project.id;
    }

    if (projectIdNum == null) {
      throw new Error('Unable to resolve app id');
    }

    const isFavorite = favorites.some(fav => fav.id === projectIdNum);

    if (isFavorite) {
      await browserDB.removeFavoriteApp(user.id, projectIdNum);
      return false;
    } else {
      await browserDB.addFavoriteApp(user.id, projectIdNum);
      return true;
    }
  } catch (err) {
    throw new Error(`Failed to toggle favorite: ${err instanceof Error ? err.message : 'Unknown error'}`);
  }
}

/**
 * Hook for protected toggle favorite functionality
 */
export function useToggleFavorite() {
  const { requireAuth } = useAuthGuard();

  const protectedToggleFavorite = async (appId: string, actionContext = 'to favorite this app'): Promise<boolean | null> => {
    if (!requireAuth({ 
      requireFullAuth: true, 
      actionContext 
    })) {
      return null;
    }

    return await toggleFavorite(appId);
  };

  return { toggleFavorite: protectedToggleFavorite };
}